import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Boton from '../components/Boton'
import { whatsappCobrosApi } from '../api/whatsappCobros'
import { useAuth } from '../context/AuthContext'
import { fmtFecha, fmtHoraDe } from '../utils/fecha'
import { colors, font, text, space, radius, sx } from '../design/tokens'

/**
 * VENTAS → Carritos abandonados → Mensajes de cobro por WhatsApp (Martin,
 * 26 sep 2026): tres instancias de cobro manuales, cada una con su texto, que
 * salen de la línea del sistema; si el cliente contesta, LGR responde una vez
 * con el link al WhatsApp de Ventas. Solo el super administrador la administra.
 *
 * WhatsApp solo deja escribirle a quien no nos escribió en las últimas 24 h
 * con una PLANTILLA aprobada por Meta — por eso cada texto se manda a aprobar
 * y el link de pago va como botón (no pegado en el texto).
 */
const EJEMPLO = { '{{nombre}}': 'Gabriel', '{{pedido}}': 'LW-000513', '{{total}}': '$1.135.910', '{{membresia}}': '' }
const EJEMPLO_MEMBRESIA = { ...EJEMPLO, '{{membresia}}': 'Gold' }
const EJEMPLO_DERIVACION = { '{{nombre}}': 'Gabriel Vittoni', '{{telefono}}': '+54 9 11 6419-9868', '{{motivo}}': 'corporativo_descuento: pide precio por volumen', '{{resumen}}': 'Quiere 10 cajas de Rutini Malbec con factura A y pago a 30 días.', '{{link}}': 'https://lgr.vinotecaligier.com/m/whatsapp/123' }
const EJEMPLO_RESUMEN = { '{{turno}}': 'lunes 28/09 18:00 a martes 29/09 10:00', '{{cantidad}}': '2', '{{resumen}}': '1. Gabriel Vittoni (+54 9 11 6419-9868): quería 6 tintos para regalar, le armé el carrito, no pagó.\n2. Ana Pérez (+54 9 11 5095-7101): pidió precio por 50 cajas, derivada a Ventas.', '{{link}}': 'https://lgr.vinotecaligier.com/m/whatsapp?turno=2026-09-29' }
const EJEMPLOS_POR_TIPO = { pedido: EJEMPLO, membresia: EJEMPLO_MEMBRESIA, derivacion: EJEMPLO_DERIVACION, resumen: EJEMPLO_RESUMEN, seleccion: { '{{nombre}}': 'Gabriel' }, regalos: {} }

// Dos juegos de plantillas (Martin, 26 sep 2026: "tiene que haber unas distintas
// para las membresías"): el backend elige el juego según lo que trae el pedido.
const TIPOS = [
  { tipo: 'pedido', titulo: 'Pedidos', nota: 'Pedidos comunes de la tienda.' },
  { tipo: 'membresia', titulo: 'Membresías Wine Club', nota: 'El pedido trae una membresía del Wine Club: el texto puede nombrar el nivel con {{membresia}}.' },
  // Agente v2 (Martin, 28 sep 2026): cuando el asistente no puede resolver, le avisa al WhatsApp de Ventas con esta plantilla.
  // Próxima Selección por WhatsApp (Martin, 4 oct 2026): el cliente eligió que lo contactemos por WhatsApp.
  { tipo: 'seleccion', titulo: 'Próxima Selección', nota: 'Mensaje 1: sale el día que eligió el cliente, con el botón de respuesta rápida. Si lo toca (o contesta), LGR le manda al instante su compra anterior a precios de hoy, las sugerencias y el link de compra (ese texto lo arma LGR, no es plantilla). Mensaje 2: solo si no contestó en 24 h, con el botón que lleva a su selección.', boton: 'Sí, mostrame' },
  // Regalos Empresariales (Martin, 26 oct 2026): presenta el catálogo; si responden, LGR deriva a Ventas.
  { tipo: 'regalos', titulo: 'Regalos Empresariales', nota: 'Campaña 2026: presenta el Catálogo de Regalos con el banner de Ligier y un botón al catálogo. Si la persona responde, LGR le pasa el WhatsApp de Ventas (6060) y deja de mandarle los mails de la campaña.', etiqueta: 'Campaña', boton: 'Ver el catálogo' },
  { tipo: 'resumen', titulo: 'Resumen del turno', nota: 'A las 10:00, de lunes a sábado, cuando Ventas toma el mando: UN mensaje con todas las conversaciones que atendió el asistente fuera de horario (una línea por cliente). Si no atendió ninguna, no se manda.', etiqueta: 'Resumen a Ventas', boton: 'Ver las conversaciones' },
  { tipo: 'derivacion', titulo: 'Derivación a Ventas', nota: 'Lo que recibe el WhatsApp de Ventas cuando el asistente no puede resolver una consulta. El teléfono del cliente va en el texto (se toca para escribirle); el botón abre la conversación en LGR. Meta no permite botones a wa.me.', etiqueta: 'Aviso a Ventas', boton: 'Ver la conversación' },
]
const clave = (tipo, etapa) => `${tipo}-${etapa}`

const ESTADO_META = {
  APPROVED: { label: 'Aprobada', bg: colors.okBg, color: colors.ok },
  PENDING: { label: 'Pendiente de aprobación', bg: colors.warnBg, color: colors.warn },
  REJECTED: { label: 'Rechazada', bg: colors.errBg, color: colors.err },
  PAUSED: { label: 'Pausada', bg: colors.warnBg, color: colors.warn },
}

function formatearTel(e164) {
  const d = String(e164 || '').replace(/\D/g, '')
  if (!d) return ''
  // 54 9 11 7054-6060
  const m = d.match(/^549?(\d{2})(\d{4})(\d{4})$/)
  return m ? `+54 9 ${m[1]} ${m[2]}-${m[3]}` : `+${d}`
}

function reemplazar(texto, valores) {
  return Object.entries(valores).reduce((t, [k, v]) => t.split(k).join(v), texto || '')
}

/** Textarea con chips de variables que se insertan donde está el cursor. */
function EditorTexto({ valor, onChange, variables, filas = 5 }) {
  const ref = useRef(null)
  const insertar = (clave) => {
    const el = ref.current
    const ini = el?.selectionStart ?? valor.length
    const fin = el?.selectionEnd ?? valor.length
    const nuevo = valor.slice(0, ini) + clave + valor.slice(fin)
    onChange(nuevo)
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(ini + clave.length, ini + clave.length) } })
  }
  return (
    <div>
      <textarea ref={ref} rows={filas} value={valor} onChange={e => onChange(e.target.value)} style={st.textarea} data-nocaps />
      <div style={st.chips}>
        {variables.map(v => (
          <button key={v.clave} type="button" style={st.chip} title={v.descripcion || ''} onClick={() => insertar(v.clave)}>{v.clave}</button>
        ))}
        <span style={st.contador}>{(valor || '').length} / 1024</span>
      </div>
    </div>
  )
}

/** Burbuja estilo WhatsApp con las variables reemplazadas por un ejemplo. */
function Burbuja({ texto, boton, imagen }) {
  return (
    <div style={st.telefono}>
      <div style={st.burbuja}>
        {imagen && <img src="/img/wa-banner-ligier.png" alt="Banner Ligier" style={st.burbujaImagen} />}
        <div style={st.burbujaTexto}>{texto || <span style={{ color: colors.textMeta }}>(sin texto)</span>}</div>
        <div style={st.burbujaHora}>10:32</div>
        {boton && <div style={st.burbujaBoton}>🔗 {boton}</div>}
      </div>
    </div>
  )
}

export default function WhatsAppCobrosPage() {
  const { isSuperAdmin, impersonateRole } = useAuth()
  const navigate = useNavigate()
  const [cfg, setCfg] = useState(null)
  const [error, setError] = useState(null)
  const [mensaje, setMensaje] = useState(null)
  const [borradores, setBorradores] = useState({})     // 'tipo-etapa' → {texto, boton_texto, con_imagen}
  const [respuesta, setRespuesta] = useState('')
  const [ocupado, setOcupado] = useState(null)         // 'sync' | 'guardar-1' | 'meta-1' | 'resp'

  const aplicar = useCallback((data) => {
    setCfg(data)
    const b = {}
    for (const p of data.plantillas || []) b[clave(p.tipo, p.etapa)] = { texto: p.texto || '', boton_texto: p.boton_texto || '', con_imagen: !!p.con_imagen }
    setBorradores(b)
    setRespuesta(data.respuesta_automatica?.texto || '')
  }, [])

  const cargar = useCallback(() => whatsappCobrosApi.config()
    .then(({ data }) => { aplicar(data); setError(null) })
    .catch(err => setError(err.response?.data?.message || 'No se pudo cargar la configuración.')), [aplicar])

  useEffect(() => { if (isSuperAdmin && !impersonateRole) cargar() }, [cargar, isSuperAdmin, impersonateRole])

  const avisar = (txt) => { setMensaje(txt); setTimeout(() => setMensaje(null), 3000) }
  const fallo = (err, fallback) => setError(err.response?.data?.message || fallback)

  const pisar = (tipo, etapa, data) => setCfg(c => ({ ...c, plantillas: c.plantillas.map(p => (p.tipo === tipo && p.etapa === etapa) ? data : p) }))

  const guardar = async (tipo, etapa) => {
    const k = clave(tipo, etapa)
    setOcupado(`guardar-${k}`); setError(null)
    try {
      const { data } = await whatsappCobrosApi.guardarPlantilla(tipo, etapa, borradores[k])
      pisar(tipo, etapa, data)
      avisar(`Mensaje ${etapa} guardado.`)
    } catch (err) { fallo(err, 'No se pudo guardar.') } finally { setOcupado(null) }
  }

  const enviarAMeta = async (tipo, etapa) => {
    const k = clave(tipo, etapa)
    setOcupado(`meta-${k}`); setError(null)
    try {
      // Guarda lo que está en pantalla antes de mandarlo a aprobar.
      const g = await whatsappCobrosApi.guardarPlantilla(tipo, etapa, borradores[k])
      pisar(tipo, etapa, g.data)
      const { data } = await whatsappCobrosApi.enviarAMeta(tipo, etapa)
      pisar(tipo, etapa, data)
      avisar(`Mensaje ${etapa} enviado a Meta — queda pendiente de aprobación.`)
    } catch (err) { fallo(err, 'Meta no aceptó la plantilla.') } finally { setOcupado(null) }
  }

  const sincronizar = async () => {
    setOcupado('sync'); setError(null)
    try { const { data } = await whatsappCobrosApi.sincronizar(); aplicar(data); avisar('Estados actualizados desde Meta.') }
    catch (err) { fallo(err, 'No se pudieron actualizar los estados.') } finally { setOcupado(null) }
  }

  const guardarRespuesta = async () => {
    setOcupado('resp'); setError(null)
    try {
      const { data } = await whatsappCobrosApi.guardarRespuestaAutomatica(respuesta)
      setCfg(c => ({ ...c, respuesta_automatica: data }))
      avisar('Respuesta automática guardada.')
    } catch (err) { fallo(err, 'No se pudo guardar.') } finally { setOcupado(null) }
  }

  if (!isSuperAdmin || impersonateRole) {
    return <div style={st.aviso}>Los mensajes de cobro por WhatsApp los administra solo el Super Administrador (con su rol real).</div>
  }
  if (error && !cfg) return <div style={st.errorBox}>{error}</div>
  if (!cfg) return <p style={{ color: colors.textSec }}>Cargando...</p>

  const telVentas = formatearTel(cfg.respuesta_automatica?.telefono_ventas)
  const ejemploRespuesta = {
    '{{pedido}}': 'LW-000513',
    '{{link}}': `https://wa.me/${String(cfg.respuesta_automatica?.telefono_ventas || '').replace(/\D/g, '')}?text=Pedido%20LW-000513`,
    '{{horario}}': cfg.respuesta_automatica?.horario || '',
  }

  return (
    <div>
      <div style={st.header}>
        <div>
          <Boton variante="ghost" sm onClick={() => navigate(-1)}>← Volver</Boton>
          <h2 style={st.title}>Mensajes de cobro por WhatsApp</h2>
          <p style={st.sub}>
            Línea del sistema: {cfg.linea ? <span style={sx.mono}>{cfg.linea.numero_visible}</span> : <span>sin línea configurada</span>}
            {cfg.linea && !cfg.linea.activa && <span style={st.badgeOff}> línea inactiva</span>}
            {!cfg.outbound && <span style={st.badgeOff}> envíos apagados (WHATSAPP_OUTBOUND_ENABLED)</span>}
          </p>
        </div>
        <Boton variante="secondary" sm disabled={ocupado === 'sync'} onClick={sincronizar}>{ocupado === 'sync' ? '…' : '↻ Actualizar estados'}</Boton>
      </div>

      <div style={st.explica}>
        WhatsApp solo deja escribirle a alguien que no nos escribió en las últimas 24 h con una <b>plantilla aprobada por Meta</b>:
        cada mensaje se guarda acá, se manda a aprobar (minutos, a veces horas) y recién aprobado se puede usar desde Carritos abandonados o la NV.
        El link de pago no va en el texto: viaja como <b>botón</b> debajo del mensaje. Los tres se mandan a mano, una vez cada uno, en el orden que decidas.
      </div>

      {mensaje && <div style={st.success}>{mensaje}</div>}
      {error && <div style={st.errorBox}>{error}</div>}

      {TIPOS.map(t => (
      <div key={t.tipo}>
      <div style={st.bloque}>
        <h3 style={st.bloqueTitulo}>{t.titulo}</h3>
        <span style={st.bloqueNota}>{t.nota}</span>
      </div>
      {(cfg.plantillas || []).filter(p => (p.tipo || 'pedido') === t.tipo).map(p => {
        const k = clave(t.tipo, p.etapa)
        const b = borradores[k] || { texto: '', boton_texto: '', con_imagen: false }
        const em = ESTADO_META[p.estado_meta]
        const setB = (cambio) => setBorradores(x => ({ ...x, [k]: { ...b, ...cambio } }))
        return (
          <div key={k} style={st.card}>
            <div style={st.cardHead}>
              <h3 style={sx.cardTitle}>{t.etiqueta ? (p.etapa > 1 ? `${t.etiqueta} · respaldo` : t.etiqueta) : `Mensaje ${p.etapa}`}</h3>
              <div style={st.estadoLinea}>
                {em
                  ? <span style={{ ...st.badge, background: em.bg, color: em.color }}>{em.label}</span>
                  : <span style={{ ...st.badge, background: colors.bgSoft, color: colors.textSec }}>Sin enviar a Meta</span>}
                {p.estado_meta === 'REJECTED' && p.motivo_rechazo && <span style={st.motivo}>{p.motivo_rechazo}</span>}
                {p.sin_enviar && em && <span style={st.cambiado}>cambios sin enviar a Meta</span>}
                {p.enviada_a_meta_at && <span style={st.metaFecha}>enviada {fmtFecha(p.enviada_a_meta_at)} {fmtHoraDe(p.enviada_a_meta_at)}</span>}
                <span style={{ ...st.metaFecha, ...sx.mono }}>{p.nombre_meta}</span>
              </div>
            </div>
            <div style={st.dos}>
              <div>
                <label style={st.label}>Texto</label>
                <EditorTexto valor={b.texto} onChange={v => setB({ texto: v })} variables={(cfg.variables || []).filter(v => (v.tipos || ['pedido', 'membresia']).includes(t.tipo))} />
                <label style={{ ...st.label, marginTop: space.md }}>Texto del botón</label>
                <input value={b.boton_texto} onChange={e => setB({ boton_texto: e.target.value })} style={st.input} maxLength={25} placeholder={t.boton || 'Completar mi pago'} data-nocaps />
                <label style={st.check}>
                  <input type="checkbox" checked={!!b.con_imagen} onChange={e => setB({ con_imagen: e.target.checked })} />
                  Con el banner de Ligier arriba
                  <span style={st.checkNota}>(cambiarlo vuelve a mandar la plantilla a aprobar)</span>
                </label>
                <div style={st.acciones}>
                  <Boton variante="primary" sm disabled={ocupado !== null} onClick={() => guardar(t.tipo, p.etapa)}>{ocupado === `guardar-${k}` ? '…' : 'Guardar'}</Boton>
                  <Boton variante="send" sm disabled={ocupado !== null || !b.texto.trim()} title="Guarda y manda la plantilla a aprobar a Meta" onClick={() => enviarAMeta(t.tipo, p.etapa)}>{ocupado === `meta-${k}` ? '…' : '→ Enviar a aprobar a Meta'}</Boton>
                </div>
              </div>
              <div>
                <label style={st.label}>Cómo lo ve el cliente</label>
                <Burbuja texto={reemplazar(b.texto, EJEMPLOS_POR_TIPO[t.tipo] || EJEMPLO)} boton={b.boton_texto || t.boton || 'Completar mi pago'} imagen={!!b.con_imagen} />
              </div>
            </div>
          </div>
        )
      })}
      </div>
      ))}

      <div style={st.card}>
        <div style={st.cardHead}>
          <h3 style={sx.cardTitle}>Respuesta automática</h3>
        </div>
        <div style={st.explicaCard}>
          Si el cliente contesta en la línea después de un cobro, LGR le responde <b>una sola vez por pedido</b> con este texto,
          con el link al WhatsApp de Ventas ({telVentas || 'sin número'}) y el horario de atención. La conversación queda igual en la bandeja.
        </div>
        <div style={st.dos}>
          <div>
            <label style={st.label}>Texto</label>
            <EditorTexto valor={respuesta} onChange={setRespuesta} variables={cfg.variables_respuesta || []} filas={4} />
            <div style={st.acciones}>
              <Boton variante="primary" sm disabled={ocupado !== null} onClick={guardarRespuesta}>{ocupado === 'resp' ? '…' : 'Guardar'}</Boton>
            </div>
          </div>
          <div>
            <label style={st.label}>Cómo lo ve el cliente</label>
            <Burbuja texto={reemplazar(respuesta, ejemploRespuesta)} />
          </div>
        </div>
      </div>
    </div>
  )
}

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: space.lg, flexWrap: 'wrap', gap: space.lg },
  title: { margin: `${space.md}px 0 0`, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: '2px 0 0', fontSize: text.sec, color: colors.textSec },
  badgeOff: { marginLeft: 8, display: 'inline-block', padding: '1px 8px', borderRadius: radius.xs, fontSize: text.meta, fontWeight: 600, background: colors.warnBg, color: colors.warn },
  explica: { fontSize: text.sec, color: colors.textSec, lineHeight: 1.5, maxWidth: 820, marginBottom: space.xl },
  explicaCard: { fontSize: text.sec, color: colors.textSec, lineHeight: 1.5, marginBottom: space.lg },
  aviso: { background: colors.warnBg, color: colors.warn, border: `1px solid ${colors.warnBorder}`, borderRadius: radius.md, padding: '12px 16px', fontSize: text.body },
  success: { background: colors.okBg, color: colors.ok, borderRadius: radius.sm, padding: '8px 12px', marginBottom: space.lg, fontSize: text.sec },
  errorBox: { background: colors.errBg, color: colors.err, borderRadius: radius.sm, padding: '8px 12px', marginBottom: space.lg, fontSize: text.sec },
  bloque: { display: 'flex', alignItems: 'baseline', gap: space.lg, margin: `${space['2xl']}px 0 ${space.md}px`, flexWrap: 'wrap' },
  bloqueTitulo: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  bloqueNota: { fontSize: text.sec, color: colors.textSec },
  card: { ...sx.card, marginBottom: space.xl },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.lg, marginBottom: space.lg, flexWrap: 'wrap' },
  estadoLinea: { display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: radius.xs, fontSize: text.meta, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' },
  motivo: { fontSize: text.sec, color: colors.err },
  cambiado: { fontSize: text.sec, color: colors.warn, fontWeight: 600 },
  metaFecha: { fontSize: text.meta, color: colors.textMeta, whiteSpace: 'nowrap' },
  dos: { display: 'grid', gridTemplateColumns: 'minmax(320px, 1fr) minmax(260px, 340px)', gap: space['2xl'], alignItems: 'start' },
  label: { display: 'block', fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: space.xs },
  textarea: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontFamily: font.sans, fontSize: text.body, lineHeight: 1.45, color: colors.text, resize: 'vertical' },
  input: { width: '100%', maxWidth: 320, boxSizing: 'border-box', padding: '6px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontFamily: font.sans, fontSize: text.body, color: colors.text },
  chips: { display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap', marginTop: space.sm },
  chip: { background: colors.bgSoft, border: `1px solid ${colors.border}`, borderRadius: radius.pill, padding: '2px 10px', fontSize: text.meta, fontFamily: font.mono, color: colors.textSub, cursor: 'pointer' },
  contador: { marginLeft: 'auto', fontSize: text.meta, color: colors.textMeta, ...sx.mono },
  acciones: { display: 'flex', gap: space.md, marginTop: space.lg, flexWrap: 'wrap' },
  // Vista previa: fondo del chat de WhatsApp + burbuja entrante (blanca).
  telefono: { background: '#e5ddd5', borderRadius: radius.md, padding: '16px 14px', border: `1px solid ${colors.border}` },
  burbuja: { background: '#ffffff', borderRadius: '0 8px 8px 8px', padding: '8px 10px 6px', maxWidth: 300, boxShadow: '0 1px 1px rgba(0,0,0,0.08)', fontFamily: font.sans },
  burbujaImagen: { display: 'block', width: '100%', borderRadius: 6, marginBottom: 6 },
  check: { display: 'flex', alignItems: 'center', gap: space.sm, marginTop: space.md, fontSize: text.sec, color: colors.text, cursor: 'pointer' },
  checkNota: { fontSize: text.meta, color: colors.textMeta },
  burbujaTexto: { fontSize: 13.5, lineHeight: 1.4, color: '#111b21', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  burbujaHora: { fontSize: 10, color: '#667781', textAlign: 'right', marginTop: 2 },
  burbujaBoton: { marginTop: 6, borderTop: '1px solid #e9edef', paddingTop: 6, textAlign: 'center', color: '#027eb5', fontSize: 13.5, fontWeight: 500 },
}
